import { useCallback, useEffect, useRef, useState } from "react";
import { Activity, RefreshCw, Database, HardDrive, RotateCcw, AlertTriangle, MemoryStick } from "lucide-react";
import { api } from "../../lib/api";

const fmtTs = (iso) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "?");

export const ServerStatusAdmin = () => {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tip, setTip] = useState(null);
  const stripRef = useRef(null);

  const showTip = (ev, c, i) => {
    const checks = h?.checks || [];
    const el = ev.currentTarget;
    const w = stripRef.current?.offsetWidth || 600;
    let lines;
    if (c.ok) {
      lines = [`${fmtTs(c.ts)} — OK`, `reactietijd ${c.latency_ms} ms`];
    } else {
      let s = i, e = i;
      while (s > 0 && !checks[s - 1].ok) s--;
      while (e < checks.length - 1 && !checks[e + 1].ok) e++;
      const endTs = checks[e + 1]?.ts;
      let mins = null;
      let endLabel = endTs ? fmtTs(endTs) : "nu (nog bezig)";
      try {
        const start = new Date(checks[s].ts);
        const lastRed = new Date(checks[e].ts);
        let end = endTs ? new Date(endTs) : new Date();
        if (end - lastRed > 10 * 60000) {
          end = new Date(lastRed.getTime() + 5 * 60000);
          endLabel = `±${fmtTs(end.toISOString())}`;
        }
        mins = Math.max(5, Math.round((end - start) / 60000));
      } catch (err) {
        mins = null;
      }
      lines = [
        "Storing — server onbereikbaar",
        `van ${fmtTs(checks[s].ts)} tot ${endLabel}`,
        `duur: ±${mins ?? "?"} min${c.error ? ` · ${String(c.error).slice(0, 40)}` : ""}`,
      ];
    }
    setTip({ left: Math.max(0, Math.min(el.offsetLeft - 100, w - 250)), top: el.offsetTop, lines });
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/health").then((r) => setH(r.data)).catch(() => setH({ status: "storing", unreachable: true })).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!h) return null;
  const ok = h.status === "ok";

  return (
    <div data-testid="admin-server-status" className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-primary" /> Serverstatus</h2>
        <button data-testid="server-status-refresh" onClick={load} title="Vernieuwen"
          className="p-2 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="bg-card rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <span data-testid="server-status-light" className={`w-4 h-4 rounded-full shrink-0 ${ok ? "bg-emerald-500 animate-pulse-soft" : "bg-red-500"}`} />
          <p className="font-heading font-bold">{ok ? "Alles operationeel" : "Storing gedetecteerd"}</p>
          {h.restarts_24h >= 4 && (
            <span data-testid="server-restart-warning" className="text-[11px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {h.restarts_24h}× herstart in 24u — mogelijk geheugentekort
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <div className="bg-secondary/60 rounded-xl px-3 py-2.5">
            <p className="text-[10px] tracking-wide uppercase font-bold text-muted-foreground flex items-center gap-1"><Database className="w-3 h-3" /> Database</p>
            <p className="font-mono font-bold text-sm">{h.db_ok ? `OK · ${h.db_latency_ms} ms` : "FOUT"}</p>
          </div>
          <div className="bg-secondary/60 rounded-xl px-3 py-2.5">
            <p className="text-[10px] tracking-wide uppercase font-bold text-muted-foreground flex items-center gap-1"><HardDrive className="w-3 h-3" /> Opslag</p>
            <p className="font-mono font-bold text-sm">{h.storage_ok ? "OK" : "FOUT"}</p>
          </div>
          <div className="bg-secondary/60 rounded-xl px-3 py-2.5">
            <p className="text-[10px] tracking-wide uppercase font-bold text-muted-foreground">Uptime 24u</p>
            <p className="font-mono font-bold text-sm" data-testid="server-uptime-pct">{h.uptime_pct_24h != null ? `${h.uptime_pct_24h}%` : "meet nog…"}{h.avg_latency_ms ? ` · ø ${h.avg_latency_ms} ms` : ""}</p>
          </div>
          <div className="bg-secondary/60 rounded-xl px-3 py-2.5">
            <p className="text-[10px] tracking-wide uppercase font-bold text-muted-foreground flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Crashes 24u</p>
            <p className="font-mono font-bold text-sm" data-testid="server-crashes-24h">{h.crashes_24h ?? h.restarts_24h ?? 0} <span className="text-muted-foreground font-normal">· {Math.max((h.starts_24h ?? 0) - (h.crashes_24h ?? 0), 0)} deploys</span></p>
            <p className="text-[10px] text-muted-foreground">online sinds {fmtTs(h.process_started)}</p>
          </div>
          <div className="bg-secondary/60 rounded-xl px-3 py-2.5">
            <p className="text-[10px] tracking-wide uppercase font-bold text-muted-foreground flex items-center gap-1"><MemoryStick className="w-3 h-3" /> Geheugen</p>
            <p className="font-mono font-bold text-sm" data-testid="server-memory-mb">{h.memory_mb != null ? `${h.memory_mb} MB` : "—"}</p>
          </div>
          <div className="bg-secondary/60 rounded-xl px-3 py-2.5">
            <p className="text-[10px] tracking-wide uppercase font-bold text-muted-foreground flex items-center gap-1"><HardDrive className="w-3 h-3" /> Foto's bespaard</p>
            <p className="font-mono font-bold text-sm" data-testid="photo-shrink-saved">{h.photo_shrink ? `${h.photo_shrink.saved_mb} MB · ${h.photo_shrink.count} foto's` : "—"}</p>
            {h.photo_shrink?.pending > 0 && <p className="text-[10px] text-muted-foreground" data-testid="photo-shrink-pending">nog {h.photo_shrink.pending} te verwerken</p>}
          </div>
        </div>
        {(h.checks || []).length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] tracking-wide uppercase font-bold text-muted-foreground mb-1.5">Bereikbaarheidschecks (elke 5 min, laatste 24u) — hover over een blokje voor details</p>
            <div ref={stripRef} className="relative">
              <div className="flex flex-wrap gap-[2px]" data-testid="server-check-strip">
                {h.checks.map((c, i) => (
                  <span key={i}
                    onMouseEnter={(ev) => showTip(ev, c, i)}
                    onMouseLeave={() => setTip(null)}
                    onClick={(ev) => showTip(ev, c, i)}
                    className={`w-1.5 h-4 rounded-sm cursor-pointer transition-transform duration-100 hover:scale-y-125 ${c.ok ? "bg-emerald-400 hover:bg-emerald-500" : "bg-red-500 hover:bg-red-600"}`} />
                ))}
              </div>
              {tip && (
                <div data-testid="server-check-tooltip" className="absolute z-30 pointer-events-none bg-foreground text-background text-[11px] font-mono rounded-lg px-3 py-2 shadow-lg whitespace-nowrap"
                  style={{ left: tip.left, top: tip.top + 22 }}>
                  {tip.lines.map((l, j) => <p key={j} className={j === 0 ? "font-bold" : ""}>{l}</p>)}
                </div>
              )}
            </div>
          </div>
        )}
        <div>
          <p className="text-[10px] tracking-wide uppercase font-bold text-muted-foreground mb-1.5">Recente incidenten</p>
          {(h.incidents || []).length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="server-no-incidents">Geen incidenten geregistreerd.</p>
          ) : (
            <div className="space-y-1" data-testid="server-incidents-list">
              {h.incidents.map((inc, i) => (
                <p key={i} className="text-xs font-mono">
                  <span className="text-red-600 font-bold">{inc.type}</span> · {fmtTs(inc.start)} → {fmtTs(inc.end)} · ±{inc.minutes} min
                </p>
              ))}
            </div>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mt-3">Bij downtime of onbereikbaarheid krijg je automatisch een e-mail (max 1 per 30 min). Elke maandagochtend ontvang je de weekstats-mail, inclusief uptime, herstarts en geheugenverbruik van de afgelopen week. Frequente herstarts wijzen op geheugentekort — verhoog dan de tier via het Deployment-paneel.</p>
      </div>
    </div>
  );
};
